import { useMemo } from 'react';
import { useTheme } from 'react-admin';
import { Calendar, DateObject } from 'react-multi-date-picker';
import { uk } from 'date-fns/locale/uk';
import { Month, Day, differenceInDays } from 'date-fns';
import { Box, IconButton } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

import '@/components/CustomCalendar/style.scss';

interface ICustomCalendar {
  selectedDates: Date[];
  multiple?: boolean;
  onChange: (dates: Date[]) => void;
  hidePastDays?: boolean;
}

function CustomCalendar({ selectedDates, multiple, onChange, hidePastDays }: ICustomCalendar) {
  const [theme] = useTheme();

  const localized = useMemo(() => {
    const weekDays = new Array(7)
      .fill(null)
      .map((_, index) => uk.localize.day(index as Day, { width: 'narrow' }));
    const months = new Array(12).fill(null).map((_, index) => uk.localize.month(index as Month));

    return {
      weekDays,
      months,
    };
  }, []);

  const handleOnChange = (value: DateObject[]) => {
    const dates = value.map(item => item?.toDate());
    onChange(dates);
  };

  return (
    <Box>
      <Calendar
        multiple={multiple}
        className={`md-calendar ${theme}`}
        value={selectedDates}
        currentDate={new DateObject()}
        onChange={handleOnChange}
        weekDays={localized.weekDays}
        months={localized.months}
        weekStartDayIndex={1}
        headerOrder={['MONTH_YEAR', 'LEFT_BUTTON', 'RIGHT_BUTTON']}
        mapDays={({ date }) => {
          if (hidePastDays) {
            const diff = differenceInDays(date.toDate(), new Date());

            return {
              disabled: diff < 0,
            };
          }
        }}
        renderButton={(direction: string, handleClick: () => void) =>
          direction === 'right' ? (
            <IconButton size="small" onClick={handleClick}>
              <NavigateNextIcon />
            </IconButton>
          ) : (
            <IconButton size="small" onClick={handleClick}>
              <NavigateBeforeIcon />
            </IconButton>
          )
        }
      />
    </Box>
  );
}

export default CustomCalendar;
